/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import { useApplicationBudgetQuery } from "~/generated/graphql";
import { generateBudget, sanitizeLastYearSpend } from "~/components/Budgets";
import { dateFormat } from "~/constants";
import { useConvertFromBaseCurrency, useCurrency } from "~/hooks";
import { generateNumericArray, isNotNil } from "~/tools";
import { useCreateBudget } from "./useCreateBudget";
import { useEditBudget } from "./useEditBudget";

export function useApplicationBudget(id: string) {
  const { currency } = useCurrency();
  const { convert, rate } = useConvertFromBaseCurrency();
  const [year, setYear] = useState(dayjs().format(dateFormat.year));
  const [budget, setBudget] = useState<string[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);

  const { data, loading, error } = useApplicationBudgetQuery({
    variables: {
      id,
      year,
      previousYear: (parseInt(year, 10) - 1).toString(),
    },
  });

  const sumOfEnvBudgets = useMemo(
    () =>
      data?.application?.environments
        ?.map((env) => env?.budgetYearly.budgetByMonth.map(convert))
        .filter(isNotNil)
        .reduce(
          (prev, curr) =>
            prev.map((value, index) => value + parseFloat(curr[index])),
          generateNumericArray(12).map(() => 0)
        ) ?? [],
    [data?.application?.environments]
  );

  const [createApplicationBudget] = useCreateBudget();
  const [updateApplicationBudget] = useEditBudget();

  const lastYearSpends = (data?.application?.spendYearly.spends ?? []).map(
    convert
  );
  const forecastSpendYearly = (
    data?.application?.forecastSpendYearly.spends ?? []
  ).map(convert);

  useEffect(() => {
    setBudget(
      (data?.application?.budgetYearly.budgetByMonth ?? []).map(convert)
    );
  }, [rate, setBudget, data]);

  return {
    budget,
    spends: lastYearSpends.map(Number),
    forecast: forecastSpendYearly.map(Number),
    sumOfEnvBudgets,
    loading,
    error,
    createApplicationBudget: async (value: number) => {
      await createApplicationBudget({
        nid: id,
        year,
        budgetValue: value,
        currency,
      });
      setIsGenerated(false);
    },
    updateApplicationBudget: async (values: number[]) => {
      await updateApplicationBudget({
        nid: id,
        year,
        budgetByMonth: values,
        currency,
      });
      setIsGenerated(false);
    },
    getGeneratedBudget: (initial = true) => {
      setBudget(
        sanitizeLastYearSpend(
          generateBudget(lastYearSpends, forecastSpendYearly, year)
        )
      );
      setIsGenerated(initial);
    },
    year,
    setYear: (value: string) => {
      setYear(value);
      setIsGenerated(false);
    },
    reset: (cached: number[][]) => {
      if (isGenerated) {
        setBudget([]);
        setIsGenerated(false);
      } else {
        setBudget(cached.flat().map(String));
      }
    },
    isBudgetGenerated: isGenerated,
  };
}
